import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Settings, 
  Activity, 
  AlertTriangle, 
  CheckCircle,
  Save,
  RefreshCw,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Users,
  Clock,
  Download,
  TestTube,
  BarChart3,
  MessageCircle,
  RotateCcw,
  XCircle
} from 'lucide-react';
import api from '../../services/api';

const SecurityManagement = () => {
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [settings, setSettings] = useState({});
  const [stats, setStats] = useState({});
  const [logs, setLogs] = useState([]);
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [failedLogins, setFailedLogins] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [recaptchaSettings, setRecaptchaSettings] = useState({
    enabled: false,
    siteKey: '',
    secretKey: '',
    pages: {
      login: false,
      register: false,
      forgotPassword: false,
      contact: false,
      resetPassword: false
    }
  });

  const tabs = [
    { id: 'settings', label: 'Security Settings', icon: Settings },
    { id: 'recaptcha', label: 'reCAPTCHA Settings', icon: Shield },
    { id: 'monitoring', label: 'Security Monitoring', icon: Activity },
    { id: 'logs', label: 'Security Logs', icon: BarChart3 },
    { id: 'ip-management', label: 'IP Management', icon: Shield },
    { id: 'sessions', label: 'Active Sessions', icon: Users },
    { id: 'testing', label: 'Security Testing', icon: TestTube }
  ];

  const testAPIConnection = async () => {
    try {
      console.log('🔒 Testing API connection...');
      
      // Test authentication
      const token = localStorage.getItem('authToken');
      console.log('🔑 Token exists:', !!token);
      
      // Test getting settings
      const response = await api.get('/security/settings');
      console.log('🔒 Settings response:', response.data);
      
      return true;
    } catch (error) {
      console.error('🔒 API connection test failed:', error);
      return false;
    }
  };

  useEffect(() => {
    loadData();
    testAPIConnection(); // Test API connection on component mount
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      console.log('🔒 Loading security data...');
      const [settingsRes, statsRes, recaptchaRes] = await Promise.all([
        api.get('/security/settings'),
        api.get('/security/stats'),
        api.get('/recaptcha/settings')
      ]);
      
      console.log('🔒 Settings response:', settingsRes.data);
      console.log('🔒 Stats response:', statsRes.data);
      console.log('🔒 reCAPTCHA response:', recaptchaRes.data);
      
      setSettings(settingsRes.data.data.settings);
      setStats(statsRes.data.data);
      
      // Load reCAPTCHA settings
      if (recaptchaRes.data.success) {
        setRecaptchaSettings(recaptchaRes.data.data);
      }
      
      console.log('🔒 Settings state updated:', settingsRes.data.data.settings);
    } catch (error) {
      console.error('Error loading security data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (category, key, value) => {
    console.log('🔒 Setting change:', { category, key, value });
    setSettings(prev => {
      const newSettings = {
        ...prev,
        [category]: {
          ...prev[category],
          [key]: value
        }
      };
      console.log('🔒 Updated settings:', newSettings);
      return newSettings;
    });
  };

  const resetToOptimalSettings = async () => {
    if (!window.confirm('Are you sure you want to reset all security settings to optimal values? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      console.log('🔒 Resetting security settings to optimal values...');
      const response = await api.post('/security/reset');
      console.log('🔒 Reset response:', response.data);
      
      if (response.data.success) {
        setSaveMessage('Security settings reset to optimal values successfully');
        setSaveStatus('success');
        // Reload settings to show the new optimal values
        await loadData();
      } else {
        setSaveMessage(response.data.message || 'Error resetting security settings');
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('🔒 Error resetting security settings:', error);
      setSaveMessage(error.response?.data?.message || 'Error resetting security settings');
      setSaveStatus('error');
    } finally {
      setLoading(false);
      setTimeout(() => {
        setSaveMessage('');
        setSaveStatus('');
      }, 3000);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      console.log('🔒 Saving security settings:', settings);
      const response = await api.put('/security/settings', { settings });
      console.log('🔒 Save response:', response.data);
      
      if (response.data.success) {
        setSaveMessage('Security settings updated successfully');
        setSaveStatus('success');
        // Reload settings to ensure we have the latest data
        await loadData();
      } else {
        setSaveMessage(response.data.message || 'Error updating security settings');
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('🔒 Error saving security settings:', error);
      setSaveMessage(error.response?.data?.message || 'Error updating security settings');
      setSaveStatus('error');
    } finally {
      setLoading(false);
      setTimeout(() => {
        setSaveMessage('');
        setSaveStatus('');
      }, 3000);
    }
  };

  const saveRecaptchaSettings = async () => {
    setLoading(true);
    try {
      console.log('🔒 Saving reCAPTCHA settings:', recaptchaSettings);
      const response = await api.put('/recaptcha/settings', { settings: recaptchaSettings });
      console.log('🔒 reCAPTCHA Save response:', response.data);
      
      if (response.data.success) {
        setSaveMessage('reCAPTCHA settings updated successfully');
        setSaveStatus('success');
        // Reload settings to ensure we have the latest data
        await loadData();
      } else {
        setSaveMessage(response.data.message || 'Failed to update reCAPTCHA settings');
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('Error saving reCAPTCHA settings:', error);
      setSaveMessage('Failed to update reCAPTCHA settings');
      setSaveStatus('error');
    } finally {
      setLoading(false);
      setTimeout(() => {
        setSaveMessage('');
        setSaveStatus('');
      }, 3000);
    }
  };

  const loadLogs = async () => {
    try {
      const response = await api.get('/security/logs');
      setLogs(response.data.data.logs);
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };

  const loadBlockedIPs = async () => {
    try {
      const response = await api.get('/security/blocked-ips');
      setBlockedIPs(response.data.data);
    } catch (error) {
      console.error('Error loading blocked IPs:', error);
    }
  };

  const loadActiveSessions = async () => {
    try {
      const response = await api.get('/security/active-sessions');
      setActiveSessions(response.data.data);
    } catch (error) {
      console.error('Error loading active sessions:', error);
    }
  };

  const blockIP = async (ipAddress, reason) => {
    try {
      await api.post('/security/block-ip', { ipAddress, reason });
      loadBlockedIPs();
    } catch (error) {
      console.error('Error blocking IP:', error);
    }
  };

  const unblockIP = async (ipAddress) => {
    try {
      await api.delete(`/security/unblock-ip/${ipAddress}`);
      loadBlockedIPs();
    } catch (error) {
      console.error('Error unblocking IP:', error);
    }
  };

  const terminateSession = async (sessionId) => {
    try {
      await api.delete(`/security/terminate-session/${sessionId}`);
      loadActiveSessions();
    } catch (error) {
      console.error('Error terminating session:', error);
    }
  };

  const testSecurity = async (testType) => {
    try {
      const response = await api.post('/security/test-security', { testType });
      console.log('Security test results:', response.data.data);
    } catch (error) {
      console.error('Error testing security:', error);
    }
  };

  // Bulk action functions
  const toggleFeatureSelection = (featureKey) => {
    setSelectedFeatures(prev => 
      prev.includes(featureKey) 
        ? prev.filter(key => key !== featureKey)
        : [...prev, featureKey]
    );
  };

  // Apply a bulk toggle to the appropriate setting path
  const applyBulkToggle = (draftSettings, featureKey, isEnabled) => {
    switch (featureKey) {
      // Top-level feature toggles
      case 'rateLimiting':
      case 'whatsappRateLimiting':
      case 'ipBlocking':
      case 'requestValidation':
      case 'sessionSecurity':
      case 'apiSecurity':
      case 'bruteForceProtection':
      case 'sqlInjectionProtection':
      case 'xssProtection':
      case 'csrfProtection':
      case 'twoFactorAuth':
      case 'passwordSecurity':
      case 'captcha':
      case 'emailSecurity':
      case 'fileSecurity':
      case 'networkSecurity':
      case 'auditLogging':
      case 'securityHeaders': {
        if (draftSettings[featureKey]) draftSettings[featureKey].enabled = isEnabled;
        break;
      }

      // Security headers sub-features
      case 'contentSecurityPolicy': {
        if (!draftSettings.securityHeaders) draftSettings.securityHeaders = {};
        draftSettings.securityHeaders.contentSecurityPolicy = {
          ...(draftSettings.securityHeaders.contentSecurityPolicy || {}),
          enabled: isEnabled
        };
        break;
      }
      case 'httpStrictTransportSecurity': {
        if (!draftSettings.securityHeaders) draftSettings.securityHeaders = {};
        draftSettings.securityHeaders.strictTransportSecurity = {
          ...(draftSettings.securityHeaders.strictTransportSecurity || {}),
          enabled: isEnabled
        };
        break;
      }
      case 'xContentTypeOptions': {
        if (!draftSettings.securityHeaders) draftSettings.securityHeaders = {};
        draftSettings.securityHeaders.xContentTypeOptions = {
          ...(draftSettings.securityHeaders.xContentTypeOptions || {}),
          enabled: isEnabled
        };
        break;
      }
      case 'permissionsPolicy': {
        if (!draftSettings.securityHeaders) draftSettings.securityHeaders = {};
        draftSettings.securityHeaders.permissionsPolicy = {
          ...(draftSettings.securityHeaders.permissionsPolicy || {}),
          enabled: isEnabled
        };
        break;
      }

      // File security flags
      case 'scanUploads':
      case 'virusScanning':
      case 'scanForMalware':
      case 'quarantineSuspicious': {
        if (!draftSettings.fileSecurity) draftSettings.fileSecurity = { enabled: true };
        draftSettings.fileSecurity[featureKey] = isEnabled;
        break;
      }

      // Network security flags
      case 'vpnDetection':
      case 'proxyDetection':
      case 'countryRestrictions':
      case 'geoLocationTracking': {
        if (!draftSettings.networkSecurity) draftSettings.networkSecurity = { enabled: true };
        draftSettings.networkSecurity[featureKey] = isEnabled;
        break;
      }

      // Email security flags
      case 'otpVerification':
      case 'emailChangeVerification':
      case 'suspiciousLoginAlerts':
      case 'multiDeviceWarning':
      case 'loginLocationAlerts': {
        if (!draftSettings.emailSecurity) draftSettings.emailSecurity = { enabled: true };
        draftSettings.emailSecurity[featureKey] = isEnabled;
        break;
      }

      // Audit logging flags
      case 'logUserActions':
      case 'logAdminActions':
      case 'logSecurityEvents':
      case 'logDataChanges':
      case 'exportEnabled': {
        if (!draftSettings.auditLogging) draftSettings.auditLogging = { enabled: true };
        draftSettings.auditLogging[featureKey] = isEnabled;
        break;
      }

      // Optional booleans present as checkboxes
      case 'requireApiKey': {
        if (!draftSettings.apiSecurity) draftSettings.apiSecurity = { enabled: true };
        draftSettings.apiSecurity.requireApiKey = isEnabled;
        break;
      }
      case 'enableCORS': {
        if (!draftSettings.apiSecurity) draftSettings.apiSecurity = { enabled: true };
        draftSettings.apiSecurity.enableCORS = isEnabled;
        break;
      }

      // Two-factor flags
      case 'requireForAdmins': {
        if (!draftSettings.twoFactorAuth) draftSettings.twoFactorAuth = { enabled: true };
        draftSettings.twoFactorAuth.requireForAdmins = isEnabled;
        break;
      }
      case 'requireForUsers': {
        if (!draftSettings.twoFactorAuth) draftSettings.twoFactorAuth = { enabled: true };
        draftSettings.twoFactorAuth.requireForUsers = isEnabled;
        break;
      }
      case 'backupCodesEnabled': {
        if (!draftSettings.twoFactorAuth) draftSettings.twoFactorAuth = { enabled: true };
        draftSettings.twoFactorAuth.backupCodesEnabled = isEnabled;
        break;
      }

      // Password complexity flags
      case 'requireUppercase':
      case 'requireLowercase':
      case 'requireNumbers':
      case 'requireSpecialChars':
      case 'preventCommonPasswords': {
        if (!draftSettings.passwordSecurity) draftSettings.passwordSecurity = { enabled: true };
        draftSettings.passwordSecurity[featureKey] = isEnabled;
        break;
      }

      // Session force logout on password change
      case 'forceLogoutOnPasswordChange': {
        if (!draftSettings.sessionSecurity) draftSettings.sessionSecurity = { enabled: true };
        draftSettings.sessionSecurity.forceLogoutOnPasswordChange = isEnabled;
        break;
      }

      // CSRF token requirement
      case 'requireToken':
      case 'requireCsrfToken': {
        if (!draftSettings.csrfProtection) draftSettings.csrfProtection = { enabled: true };
        draftSettings.csrfProtection.requireToken = isEnabled;
        break;
      }

      // SQLi flags
      case 'logSuspiciousActivity':
      case 'blockSuspiciousQueries': {
        if (!draftSettings.sqlInjectionProtection) draftSettings.sqlInjectionProtection = { enabled: true };
        draftSettings.sqlInjectionProtection[featureKey] = isEnabled;
        break;
      }

      // XSS flags
      case 'blockScriptTags':
      case 'sanitizeInputs': {
        if (!draftSettings.xssProtection) draftSettings.xssProtection = { enabled: true };
        draftSettings.xssProtection[featureKey] = isEnabled;
        break;
      }

      // Rate limiter flags
      case 'skipSuccessfulRequests': {
        if (!draftSettings.rateLimiting) draftSettings.rateLimiting = { enabled: true };
        draftSettings.rateLimiting.skipSuccessfulRequests = isEnabled;
        break;
      }
      case 'whatsappSkipSuccessfulRequests': {
        if (!draftSettings.whatsappRateLimiting) draftSettings.whatsappRateLimiting = { enabled: true };
        draftSettings.whatsappRateLimiting.skipSuccessfulRequests = isEnabled;
        break;
      }

      // CAPTCHA flags
      case 'requireOnLogin':
      case 'requireOnRegister':
      case 'requireOnContact':
      case 'requireOnPasswordReset': {
        if (!draftSettings.captcha) draftSettings.captcha = { enabled: true };
        draftSettings.captcha[featureKey] = isEnabled;
        break;
      }

      default: {
        // No-op for unknown keys
        break;
      }
    }
  };

  const selectAllFeatures = () => {
    const allFeatures = [
      'rateLimiting',
      'whatsappRateLimiting',
      'ipBlocking',
      'requestValidation',
      'sessionSecurity',
      'apiSecurity',
      'bruteForceProtection',
      'sqlInjectionProtection',
      'xssProtection',
      'csrfProtection',
      'twoFactorAuth',
      'passwordSecurity',
      'captcha',
      'emailSecurity',
      'fileSecurity',
      'networkSecurity',
      'auditLogging',
      'securityHeaders',
      'contentSecurityPolicy',
      'httpStrictTransportSecurity',
      'xContentTypeOptions',
      'permissionsPolicy',
      'scanUploads',
      'virusScanning',
      'scanForMalware',
      'quarantineSuspicious',
      'vpnDetection',
      'proxyDetection',
      'countryRestrictions',
      'geoLocationTracking',
      'otpVerification',
      'emailChangeVerification',
      'suspiciousLoginAlerts',
      'multiDeviceWarning',
      'loginLocationAlerts',
      'logUserActions',
      'logAdminActions',
      'logSecurityEvents',
      'logDataChanges',
      'exportEnabled',
      'requireApiKey',
      'enableCORS',
      'require2FAForUsers',
      'requireUppercase',
      'requireLowercase',
      'requireNumbers',
      'requireSpecialChars',
      'preventCommonPasswords',
      'requireForAdmins',
      'requireForUsers',
      'backupCodesEnabled',
      'forceLogoutOnPasswordChange',
      'requireCsrfToken',
      'logSuspiciousActivity',
      'blockSuspiciousQueries',
      'blockScriptTags',
      'sanitizeInputs',
      'skipSuccessfulRequests',
      'whatsappSkipSuccessfulRequests',
      'requireOnLogin',
      'requireOnRegister',
      'requireOnContact',
      'requireOnPasswordReset'
    ];
    setSelectedFeatures(allFeatures);
  };

  const clearSelection = () => {
    setSelectedFeatures([]);
  };

  const bulkEnableFeatures = async () => {
    if (selectedFeatures.length === 0) {
      setSaveMessage('Please select features to enable');
      setSaveStatus('error');
      return;
    }

    setLoading(true);
    try {
      const updatedSettings = { ...settings };
      selectedFeatures.forEach(feature => applyBulkToggle(updatedSettings, feature, true));

      console.log('🔒 Bulk enabling features:', selectedFeatures);
      const response = await api.put('/security/settings', { settings: updatedSettings });
      
      if (response.data.success) {
        setSaveMessage(`Successfully enabled ${selectedFeatures.length} security features`);
        setSaveStatus('success');
        setSettings(updatedSettings);
        setSelectedFeatures([]);
        setShowBulkActions(false);
      } else {
        setSaveMessage(response.data.message || 'Error enabling features');
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('🔒 Error bulk enabling features:', error);
      setSaveMessage(error.response?.data?.message || 'Error enabling features');
      setSaveStatus('error');
    } finally {
      setLoading(false);
      setTimeout(() => {
        setSaveMessage('');
        setSaveStatus('');
      }, 3000);
    }
  };

  const bulkDisableFeatures = async () => {
    if (selectedFeatures.length === 0) {
      setSaveMessage('Please select features to disable');
      setSaveStatus('error');
      return;
    }

    setLoading(true);
    try {
      const updatedSettings = { ...settings };
      selectedFeatures.forEach(feature => applyBulkToggle(updatedSettings, feature, false));

      console.log('🔒 Bulk disabling features:', selectedFeatures);
      const response = await api.put('/security/settings', { settings: updatedSettings });
      
      if (response.data.success) {
        setSaveMessage(`Successfully disabled ${selectedFeatures.length} security features`);
        setSaveStatus('success');
        setSettings(updatedSettings);
        setSelectedFeatures([]);
        setShowBulkActions(false);
      } else {
        setSaveMessage(response.data.message || 'Error disabling features');
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('🔒 Error bulk disabling features:', error);
      setSaveMessage(error.response?.data?.message || 'Error disabling features');
      setSaveStatus('error');
    } finally {
      setLoading(false);
      setTimeout(() => {
        setSaveMessage('');
        setSaveStatus('');
      }, 3000);
    }
  };

  const renderSettings = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
        <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Security Settings</h4>
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Configure security features to protect your application from various threats.
        </p>
      </div>

      {/* Bulk Actions */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Bulk Actions</h3>
          <button
            onClick={() => setShowBulkActions(!showBulkActions)}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white rounded-lg transition-colors duration-200"
          >
            {showBulkActions ? 'Hide Bulk Actions' : 'Show Bulk Actions'}
          </button>
        </div>

        {showBulkActions && (
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={selectAllFeatures}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors duration-200"
              >
                Select All
              </button>
              <button
                onClick={clearSelection}
                className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm transition-colors duration-200"
              >
                Clear Selection
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {selectedFeatures.length} features selected
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {[
                { key: 'rateLimiting', label: 'Rate Limiting' },
                { key: 'whatsappRateLimiting', label: 'WhatsApp Rate Limiting' },
                { key: 'ipBlocking', label: 'IP Blocking' },
                { key: 'requestValidation', label: 'Request Validation' },
                { key: 'sessionSecurity', label: 'Session Security' },
                { key: 'apiSecurity', label: 'API Security' },
                { key: 'bruteForceProtection', label: 'Brute Force Protection' },
                { key: 'sqlInjectionProtection', label: 'SQL Injection Protection' },
                { key: 'xssProtection', label: 'XSS Protection' },
                { key: 'csrfProtection', label: 'CSRF Protection' },
                { key: 'twoFactorAuth', label: 'Two-Factor Auth' },
                { key: 'passwordSecurity', label: 'Password Security' },
                { key: 'captcha', label: 'CAPTCHA' },
                { key: 'emailSecurity', label: 'Email Security' },
                { key: 'fileSecurity', label: 'File Security' },
                { key: 'networkSecurity', label: 'Network Security' },
                { key: 'auditLogging', label: 'Audit Logging' },
                { key: 'securityHeaders', label: 'Security Headers' },
                { key: 'contentSecurityPolicy', label: 'Content Security Policy' },
                { key: 'httpStrictTransportSecurity', label: 'HSTS' },
                { key: 'xContentTypeOptions', label: 'X-Content-Type-Options' },
                { key: 'permissionsPolicy', label: 'Permissions Policy' },
                // Granular flags
                { key: 'scanUploads', label: 'Scan Uploads' },
                { key: 'virusScanning', label: 'Virus Scanning' },
                { key: 'scanForMalware', label: 'Scan For Malware' },
                { key: 'quarantineSuspicious', label: 'Quarantine Suspicious' },
                { key: 'vpnDetection', label: 'VPN Detection' },
                { key: 'proxyDetection', label: 'Proxy Detection' },
                { key: 'countryRestrictions', label: 'Country Restrictions' },
                { key: 'geoLocationTracking', label: 'Geo Location Tracking' },
                { key: 'otpVerification', label: 'OTP Verification' },
                { key: 'emailChangeVerification', label: 'Email Change Verification' },
                { key: 'suspiciousLoginAlerts', label: 'Suspicious Login Alerts' },
                { key: 'multiDeviceWarning', label: 'Multi-Device Warning' },
                { key: 'loginLocationAlerts', label: 'Login Location Alerts' },
                { key: 'logUserActions', label: 'Log User Actions' },
                { key: 'logAdminActions', label: 'Log Admin Actions' },
                { key: 'logSecurityEvents', label: 'Log Security Events' },
                { key: 'logDataChanges', label: 'Log Data Changes' },
                { key: 'exportEnabled', label: 'Allow Export' },
                { key: 'requireApiKey', label: 'Require API Key' },
                { key: 'enableCORS', label: 'Enable CORS' },
                { key: 'require2FAForUsers', label: 'Require 2FA for Users' },
                { key: 'requireUppercase', label: 'Require Uppercase' },
                { key: 'requireLowercase', label: 'Require Lowercase' },
                { key: 'requireNumbers', label: 'Require Numbers' },
                { key: 'requireSpecialChars', label: 'Require Special Characters' },
                { key: 'preventCommonPasswords', label: 'Prevent Common Passwords' },
                { key: 'requireForAdmins', label: 'Require 2FA for Admins' },
                { key: 'requireForUsers', label: 'Require 2FA for Users' },
                { key: 'backupCodesEnabled', label: 'Enable Backup Codes' },
                { key: 'forceLogoutOnPasswordChange', label: 'Force Logout on Password Change' },
                { key: 'requireCsrfToken', label: 'Require CSRF Token' },
                { key: 'logSuspiciousActivity', label: 'Log Suspicious Activity' },
                { key: 'blockSuspiciousQueries', label: 'Block Suspicious Queries' },
                { key: 'blockScriptTags', label: 'Block Script Tags' },
                { key: 'sanitizeInputs', label: 'Sanitize Inputs' },
                { key: 'skipSuccessfulRequests', label: 'Skip Successful Requests' },
                { key: 'whatsappSkipSuccessfulRequests', label: 'WhatsApp Skip Successful Requests' },
                { key: 'requireOnLogin', label: 'Require on Login' },
                { key: 'requireOnRegister', label: 'Require on Register' },
                { key: 'requireOnContact', label: 'Require on Contact' },
                { key: 'requireOnPasswordReset', label: 'Require on Password Reset' }
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedFeatures.includes(key)}
                    onChange={() => toggleFeatureSelection(key)}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                </label>
              ))}
            </div>

            <div className="flex space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={bulkEnableFeatures}
                disabled={loading || selectedFeatures.length === 0}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white rounded-lg disabled:opacity-50 transition-colors duration-200 flex items-center space-x-2"
              >
                <CheckCircle className="h-4 w-4" />
                <span>Enable Selected ({selectedFeatures.length})</span>
              </button>
              <button
                onClick={bulkDisableFeatures}
                disabled={loading || selectedFeatures.length === 0}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white rounded-lg disabled:opacity-50 transition-colors duration-200 flex items-center space-x-2"
              >
                <XCircle className="h-4 w-4" />
                <span>Disable Selected ({selectedFeatures.length})</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Rate Limiting */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">General Rate Limiting</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable Rate Limiting
            </label>
            <input
              type="checkbox"
              checked={settings.rateLimiting?.enabled || false}
              onChange={(e) => handleSettingChange('rateLimiting', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max Requests per Window
            </label>
            <input
              type="number"
              value={settings.rateLimiting?.max || 100}
              onChange={(e) => handleSettingChange('rateLimiting', 'max', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
        </div>
      </div>

      {/* WhatsApp Rate Limiting */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <MessageCircle className="h-5 w-5 mr-2 text-green-600" />
          WhatsApp Rate Limiting
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Special rate limiting settings for WhatsApp chat endpoints to prevent polling issues.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable WhatsApp Rate Limiting
            </label>
            <input
              type="checkbox"
              checked={settings.whatsappRateLimiting?.enabled || false}
              onChange={(e) => handleSettingChange('whatsappRateLimiting', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max WhatsApp Requests per Window
            </label>
            <input
              type="number"
              value={settings.whatsappRateLimiting?.max || 10000}
              onChange={(e) => handleSettingChange('whatsappRateLimiting', 'max', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Window Duration (minutes)
            </label>
            <input
              type="number"
              value={Math.round((settings.whatsappRateLimiting?.windowMs || 300000) / 60000)}
              onChange={(e) => handleSettingChange('whatsappRateLimiting', 'windowMs', parseInt(e.target.value) * 60000)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Skip Successful Requests
            </label>
            <input
              type="checkbox"
              checked={settings.whatsappRateLimiting?.skipSuccessfulRequests || false}
              onChange={(e) => handleSettingChange('whatsappRateLimiting', 'skipSuccessfulRequests', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
        </div>
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>Recommended:</strong> 10,000 requests per 5 minutes for development, 2,000 for production.
          </p>
        </div>
      </div>

      {/* Brute Force Protection */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Brute Force Protection</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable Brute Force Protection
            </label>
            <input
              type="checkbox"
              checked={settings.bruteForceProtection?.enabled || false}
              onChange={(e) => handleSettingChange('bruteForceProtection', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max Login Attempts
            </label>
            <input
              type="number"
              value={settings.bruteForceProtection?.maxLoginAttempts || 5}
              onChange={(e) => handleSettingChange('bruteForceProtection', 'maxLoginAttempts', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
        </div>
      </div>

      {/* XSS Protection */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">XSS Protection</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable XSS Protection
            </label>
            <input
              type="checkbox"
              checked={settings.xssProtection?.enabled || false}
              onChange={(e) => handleSettingChange('xssProtection', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Block Script Tags
            </label>
            <input
              type="checkbox"
              checked={settings.xssProtection?.blockScriptTags || false}
              onChange={(e) => handleSettingChange('xssProtection', 'blockScriptTags', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Sanitize Inputs
            </label>
            <input
              type="checkbox"
              checked={settings.xssProtection?.sanitizeInputs || false}
              onChange={(e) => handleSettingChange('xssProtection', 'sanitizeInputs', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
        </div>
      </div>

      {/* SQL Injection Protection */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">SQL Injection Protection</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable SQL Injection Protection
            </label>
            <input
              type="checkbox"
              checked={settings.sqlInjectionProtection?.enabled || false}
              onChange={(e) => handleSettingChange('sqlInjectionProtection', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Block Suspicious Queries
            </label>
            <input
              type="checkbox"
              checked={settings.sqlInjectionProtection?.blockSuspiciousQueries || false}
              onChange={(e) => handleSettingChange('sqlInjectionProtection', 'blockSuspiciousQueries', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Log Suspicious Activity
            </label>
            <input
              type="checkbox"
              checked={settings.sqlInjectionProtection?.logSuspiciousActivity || false}
              onChange={(e) => handleSettingChange('sqlInjectionProtection', 'logSuspiciousActivity', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
        </div>
      </div>

      {/* CSRF Protection */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">CSRF Protection</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable CSRF Protection
            </label>
            <input
              type="checkbox"
              checked={settings.csrfProtection?.enabled || false}
              onChange={(e) => handleSettingChange('csrfProtection', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Require CSRF Token
            </label>
            <input
              type="checkbox"
              checked={settings.csrfProtection?.requireToken || false}
              onChange={(e) => handleSettingChange('csrfProtection', 'requireToken', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Token Expiry (hours)
            </label>
            <input
              type="number"
              value={Math.round((settings.csrfProtection?.tokenExpiry || 86400000) / 3600000)}
              onChange={(e) => handleSettingChange('csrfProtection', 'tokenExpiry', parseInt(e.target.value) * 3600000)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
        </div>
      </div>

      {/* Session Security */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Session Security</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable Session Security
            </label>
            <input
              type="checkbox"
              checked={settings.sessionSecurity?.enabled || false}
              onChange={(e) => handleSettingChange('sessionSecurity', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Session Timeout (minutes)
            </label>
            <input
              type="number"
              value={Math.round((settings.sessionSecurity?.sessionTimeout || 1800000) / 60000)}
              onChange={(e) => handleSettingChange('sessionSecurity', 'sessionTimeout', parseInt(e.target.value) * 60000)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max Concurrent Sessions
            </label>
            <input
              type="number"
              value={settings.sessionSecurity?.maxConcurrentSessions || 3}
              onChange={(e) => handleSettingChange('sessionSecurity', 'maxConcurrentSessions', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Force Logout on Password Change
            </label>
            <input
              type="checkbox"
              checked={settings.sessionSecurity?.forceLogoutOnPasswordChange || false}
              onChange={(e) => handleSettingChange('sessionSecurity', 'forceLogoutOnPasswordChange', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
        </div>
      </div>

      {/* API Security */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">API Security</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable API Security
            </label>
            <input
              type="checkbox"
              checked={settings.apiSecurity?.enabled || false}
              onChange={(e) => handleSettingChange('apiSecurity', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Require API Key
            </label>
            <input
              type="checkbox"
              checked={settings.apiSecurity?.requireApiKey || false}
              onChange={(e) => handleSettingChange('apiSecurity', 'requireApiKey', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              API Key Header
            </label>
            <input
              type="text"
              value={settings.apiSecurity?.apiKeyHeader || 'X-API-Key'}
              onChange={(e) => handleSettingChange('apiSecurity', 'apiKeyHeader', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable CORS
            </label>
            <input
              type="checkbox"
              checked={settings.apiSecurity?.enableCORS || false}
              onChange={(e) => handleSettingChange('apiSecurity', 'enableCORS', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Allowed Origins (comma-separated)
          </label>
          <textarea
            value={Array.isArray(settings.apiSecurity?.allowedOrigins) ? settings.apiSecurity.allowedOrigins.join(', ') : ''}
            onChange={(e) => handleSettingChange('apiSecurity', 'allowedOrigins', e.target.value.split(',').map(origin => origin.trim()))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            rows="3"
            placeholder="http://localhost:3000, https://niswainfo.com"
          />
        </div>
      </div>

      {/* Request Validation */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Request Validation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable Request Validation
            </label>
            <input
              type="checkbox"
              checked={settings.requestValidation?.enabled || false}
              onChange={(e) => handleSettingChange('requestValidation', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max Body Size
            </label>
            <input
              type="text"
              value={settings.requestValidation?.maxBodySize || '10mb'}
              onChange={(e) => handleSettingChange('requestValidation', 'maxBodySize', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max File Size (MB)
            </label>
            <input
              type="number"
              value={Math.round((settings.requestValidation?.maxFileSize || 10485760) / 1048576)}
              onChange={(e) => handleSettingChange('requestValidation', 'maxFileSize', parseInt(e.target.value) * 1048576)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Allowed Content Types (comma-separated)
          </label>
          <textarea
            value={Array.isArray(settings.requestValidation?.allowedContentTypes) ? settings.requestValidation.allowedContentTypes.join(', ') : ''}
            onChange={(e) => handleSettingChange('requestValidation', 'allowedContentTypes', e.target.value.split(',').map(type => type.trim()))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            rows="3"
            placeholder="application/json, application/x-www-form-urlencoded, multipart/form-data"
          />
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Allowed File Types (comma-separated)
          </label>
          <textarea
            value={Array.isArray(settings.requestValidation?.allowedFileTypes) ? settings.requestValidation.allowedFileTypes.join(', ') : ''}
            onChange={(e) => handleSettingChange('requestValidation', 'allowedFileTypes', e.target.value.split(',').map(type => type.trim()))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            rows="3"
            placeholder="jpg, jpeg, png, gif, pdf, doc, docx, zip"
          />
        </div>
      </div>

      {/* IP Blocking */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">IP Blocking</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable IP Blocking
            </label>
            <input
              type="checkbox"
              checked={settings.ipBlocking?.enabled || false}
              onChange={(e) => handleSettingChange('ipBlocking', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max Failed Attempts
            </label>
            <input
              type="number"
              value={settings.ipBlocking?.maxFailedAttempts || 5}
              onChange={(e) => handleSettingChange('ipBlocking', 'maxFailedAttempts', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Block Duration (hours)
            </label>
            <input
              type="number"
              value={Math.round((settings.ipBlocking?.blockDuration || 86400000) / 3600000)}
              onChange={(e) => handleSettingChange('ipBlocking', 'blockDuration', parseInt(e.target.value) * 3600000)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Blocked IPs (comma-separated)
          </label>
          <textarea
            value={Array.isArray(settings.ipBlocking?.blockedIPs) ? settings.ipBlocking.blockedIPs.join(', ') : ''}
            onChange={(e) => handleSettingChange('ipBlocking', 'blockedIPs', e.target.value.split(',').map(ip => ip.trim()))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            rows="3"
            placeholder="192.168.1.1, 10.0.0.1"
          />
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Whitelisted IPs (comma-separated)
          </label>
          <textarea
            value={Array.isArray(settings.ipBlocking?.whitelistedIPs) ? settings.ipBlocking.whitelistedIPs.join(', ') : ''}
            onChange={(e) => handleSettingChange('ipBlocking', 'whitelistedIPs', e.target.value.split(',').map(ip => ip.trim()))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            rows="3"
            placeholder="127.0.0.1, ::1"
          />
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Two-Factor Authentication</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable Two-Factor Authentication
            </label>
            <input
              type="checkbox"
              checked={settings.twoFactorAuth?.enabled || false}
              onChange={(e) => handleSettingChange('twoFactorAuth', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Require for Admins
            </label>
            <input
              type="checkbox"
              checked={settings.twoFactorAuth?.requireForAdmins || false}
              onChange={(e) => handleSettingChange('twoFactorAuth', 'requireForAdmins', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Require for Users
            </label>
            <input
              type="checkbox"
              checked={settings.twoFactorAuth?.requireForUsers || false}
              onChange={(e) => handleSettingChange('twoFactorAuth', 'requireForUsers', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable Backup Codes
            </label>
            <input
              type="checkbox"
              checked={settings.twoFactorAuth?.backupCodesEnabled || false}
              onChange={(e) => handleSettingChange('twoFactorAuth', 'backupCodesEnabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Number of Backup Codes
            </label>
            <input
              type="number"
              value={settings.twoFactorAuth?.backupCodesCount || 10}
              onChange={(e) => handleSettingChange('twoFactorAuth', 'backupCodesCount', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
        </div>
      </div>

      {/* Password Security */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Password Security</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable Password Security
            </label>
            <input
              type="checkbox"
              checked={settings.passwordSecurity?.enabled || false}
              onChange={(e) => handleSettingChange('passwordSecurity', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Minimum Length
            </label>
            <input
              type="number"
              value={settings.passwordSecurity?.minLength || 8}
              onChange={(e) => handleSettingChange('passwordSecurity', 'minLength', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Require Uppercase
            </label>
            <input
              type="checkbox"
              checked={settings.passwordSecurity?.requireUppercase || false}
              onChange={(e) => handleSettingChange('passwordSecurity', 'requireUppercase', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Require Lowercase
            </label>
            <input
              type="checkbox"
              checked={settings.passwordSecurity?.requireLowercase || false}
              onChange={(e) => handleSettingChange('passwordSecurity', 'requireLowercase', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Require Numbers
            </label>
            <input
              type="checkbox"
              checked={settings.passwordSecurity?.requireNumbers || false}
              onChange={(e) => handleSettingChange('passwordSecurity', 'requireNumbers', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Require Special Characters
            </label>
            <input
              type="checkbox"
              checked={settings.passwordSecurity?.requireSpecialChars || false}
              onChange={(e) => handleSettingChange('passwordSecurity', 'requireSpecialChars', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Prevent Common Passwords
            </label>
            <input
              type="checkbox"
              checked={settings.passwordSecurity?.preventCommonPasswords || false}
              onChange={(e) => handleSettingChange('passwordSecurity', 'preventCommonPasswords', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Password History Count
            </label>
            <input
              type="number"
              value={settings.passwordSecurity?.passwordHistoryCount || 5}
              onChange={(e) => handleSettingChange('passwordSecurity', 'passwordHistoryCount', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Password Max Age (days)
            </label>
            <input
              type="number"
              value={Math.round((settings.passwordSecurity?.maxAge || 7776000000) / 86400000)}
              onChange={(e) => handleSettingChange('passwordSecurity', 'maxAge', parseInt(e.target.value) * 86400000)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
        </div>
      </div>

      {/* CAPTCHA */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">CAPTCHA Protection</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable CAPTCHA
            </label>
            <input
              type="checkbox"
              checked={settings.captcha?.enabled || false}
              onChange={(e) => handleSettingChange('captcha', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              CAPTCHA Provider
            </label>
            <select
              value={settings.captcha?.provider || 'recaptcha'}
              onChange={(e) => handleSettingChange('captcha', 'provider', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            >
              <option value="recaptcha">Google reCAPTCHA</option>
              <option value="hcaptcha">hCaptcha</option>
              <option value="custom">Custom CAPTCHA</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Site Key
            </label>
            <input
              type="text"
              value={settings.captcha?.siteKey || ''}
              onChange={(e) => handleSettingChange('captcha', 'siteKey', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
              placeholder="6Lc...your-site-key"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Secret Key
            </label>
            <input
              type="password"
              value={settings.captcha?.secretKey || ''}
              onChange={(e) => handleSettingChange('captcha', 'secretKey', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
              placeholder="6Lc...your-secret-key"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Require on Login
            </label>
            <input
              type="checkbox"
              checked={settings.captcha?.requireOnLogin || false}
              onChange={(e) => handleSettingChange('captcha', 'requireOnLogin', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Require on Register
            </label>
            <input
              type="checkbox"
              checked={settings.captcha?.requireOnRegister || false}
              onChange={(e) => handleSettingChange('captcha', 'requireOnRegister', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Require on Contact
            </label>
            <input
              type="checkbox"
              checked={settings.captcha?.requireOnContact || false}
              onChange={(e) => handleSettingChange('captcha', 'requireOnContact', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Require on Password Reset
            </label>
            <input
              type="checkbox"
              checked={settings.captcha?.requireOnPasswordReset || false}
              onChange={(e) => handleSettingChange('captcha', 'requireOnPasswordReset', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
        </div>
      </div>

      {/* Email Security */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Email Security</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable Email Security
            </label>
            <input
              type="checkbox"
              checked={settings.emailSecurity?.enabled || false}
              onChange={(e) => handleSettingChange('emailSecurity', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              OTP Verification
            </label>
            <input
              type="checkbox"
              checked={settings.emailSecurity?.otpVerification || false}
              onChange={(e) => handleSettingChange('emailSecurity', 'otpVerification', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Change Verification
            </label>
            <input
              type="checkbox"
              checked={settings.emailSecurity?.emailChangeVerification || false}
              onChange={(e) => handleSettingChange('emailSecurity', 'emailChangeVerification', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Suspicious Login Alerts
            </label>
            <input
              type="checkbox"
              checked={settings.emailSecurity?.suspiciousLoginAlerts || false}
              onChange={(e) => handleSettingChange('emailSecurity', 'suspiciousLoginAlerts', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Multi-Device Warning
            </label>
            <input
              type="checkbox"
              checked={settings.emailSecurity?.multiDeviceWarning || false}
              onChange={(e) => handleSettingChange('emailSecurity', 'multiDeviceWarning', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Login Location Alerts
            </label>
            <input
              type="checkbox"
              checked={settings.emailSecurity?.loginLocationAlerts || false}
              onChange={(e) => handleSettingChange('emailSecurity', 'loginLocationAlerts', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
        </div>
      </div>

      {/* File Security */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">File Security</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable File Security
            </label>
            <input
              type="checkbox"
              checked={settings.fileSecurity?.enabled || false}
              onChange={(e) => handleSettingChange('fileSecurity', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Scan Uploads
            </label>
            <input
              type="checkbox"
              checked={settings.fileSecurity?.scanUploads || false}
              onChange={(e) => handleSettingChange('fileSecurity', 'scanUploads', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Virus Scanning
            </label>
            <input
              type="checkbox"
              checked={settings.fileSecurity?.virusScanning || false}
              onChange={(e) => handleSettingChange('fileSecurity', 'virusScanning', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Scan for Malware
            </label>
            <input
              type="checkbox"
              checked={settings.fileSecurity?.scanForMalware || false}
              onChange={(e) => handleSettingChange('fileSecurity', 'scanForMalware', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Quarantine Suspicious Files
            </label>
            <input
              type="checkbox"
              checked={settings.fileSecurity?.quarantineSuspicious || false}
              onChange={(e) => handleSettingChange('fileSecurity', 'quarantineSuspicious', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max File Size (MB)
            </label>
            <input
              type="number"
              value={Math.round((settings.fileSecurity?.maxFileSize || 10485760) / 1048576)}
              onChange={(e) => handleSettingChange('fileSecurity', 'maxFileSize', parseInt(e.target.value) * 1048576)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Allowed File Extensions (comma-separated)
          </label>
          <textarea
            value={Array.isArray(settings.fileSecurity?.allowedExtensions) ? settings.fileSecurity.allowedExtensions.join(', ') : ''}
            onChange={(e) => handleSettingChange('fileSecurity', 'allowedExtensions', e.target.value.split(',').map(ext => ext.trim()))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            rows="3"
            placeholder="jpg, jpeg, png, gif, pdf, doc, docx, zip"
          />
        </div>
      </div>

      {/* Network Security */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Network Security</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable Network Security
            </label>
            <input
              type="checkbox"
              checked={settings.networkSecurity?.enabled || false}
              onChange={(e) => handleSettingChange('networkSecurity', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              VPN Detection
            </label>
            <input
              type="checkbox"
              checked={settings.networkSecurity?.vpnDetection || false}
              onChange={(e) => handleSettingChange('networkSecurity', 'vpnDetection', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Proxy Detection
            </label>
            <input
              type="checkbox"
              checked={settings.networkSecurity?.proxyDetection || false}
              onChange={(e) => handleSettingChange('networkSecurity', 'proxyDetection', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Country Restrictions
            </label>
            <input
              type="checkbox"
              checked={settings.networkSecurity?.countryRestrictions || false}
              onChange={(e) => handleSettingChange('networkSecurity', 'countryRestrictions', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Geo Location Tracking
            </label>
            <input
              type="checkbox"
              checked={settings.networkSecurity?.geoLocationTracking || false}
              onChange={(e) => handleSettingChange('networkSecurity', 'geoLocationTracking', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Allowed Countries (comma-separated)
          </label>
          <textarea
            value={Array.isArray(settings.networkSecurity?.allowedCountries) ? settings.networkSecurity.allowedCountries.join(', ') : ''}
            onChange={(e) => handleSettingChange('networkSecurity', 'allowedCountries', e.target.value.split(',').map(country => country.trim()))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            rows="3"
            placeholder="US, CA, GB, IN"
          />
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Blocked Countries (comma-separated)
          </label>
          <textarea
            value={Array.isArray(settings.networkSecurity?.blockedCountries) ? settings.networkSecurity.blockedCountries.join(', ') : ''}
            onChange={(e) => handleSettingChange('networkSecurity', 'blockedCountries', e.target.value.split(',').map(country => country.trim()))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            rows="3"
            placeholder="CN, RU, NK"
          />
        </div>
      </div>

      {/* Audit Logging */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Audit Logging</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable Audit Logging
            </label>
            <input
              type="checkbox"
              checked={settings.auditLogging?.enabled || false}
              onChange={(e) => handleSettingChange('auditLogging', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Log User Actions
            </label>
            <input
              type="checkbox"
              checked={settings.auditLogging?.logUserActions || false}
              onChange={(e) => handleSettingChange('auditLogging', 'logUserActions', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Log Admin Actions
            </label>
            <input
              type="checkbox"
              checked={settings.auditLogging?.logAdminActions || false}
              onChange={(e) => handleSettingChange('auditLogging', 'logAdminActions', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Log Security Events
            </label>
            <input
              type="checkbox"
              checked={settings.auditLogging?.logSecurityEvents || false}
              onChange={(e) => handleSettingChange('auditLogging', 'logSecurityEvents', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Log Data Changes
            </label>
            <input
              type="checkbox"
              checked={settings.auditLogging?.logDataChanges || false}
              onChange={(e) => handleSettingChange('auditLogging', 'logDataChanges', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable Export
            </label>
            <input
              type="checkbox"
              checked={settings.auditLogging?.exportEnabled || false}
              onChange={(e) => handleSettingChange('auditLogging', 'exportEnabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Retention Days
            </label>
            <input
              type="number"
              value={settings.auditLogging?.retentionDays || 365}
              onChange={(e) => handleSettingChange('auditLogging', 'retentionDays', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
        </div>
      </div>

      {/* Security Headers */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Security Headers</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable Security Headers
            </label>
            <input
              type="checkbox"
              checked={settings.securityHeaders?.enabled || false}
              onChange={(e) => handleSettingChange('securityHeaders', 'enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable Content Security Policy
            </label>
            <input
              type="checkbox"
              checked={settings.securityHeaders?.contentSecurityPolicy?.enabled || false}
              onChange={(e) => handleSettingChange('securityHeaders', 'contentSecurityPolicy', { ...settings.securityHeaders?.contentSecurityPolicy, enabled: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable HSTS
            </label>
            <input
              type="checkbox"
              checked={settings.securityHeaders?.strictTransportSecurity?.enabled || false}
              onChange={(e) => handleSettingChange('securityHeaders', 'strictTransportSecurity', { ...settings.securityHeaders?.strictTransportSecurity, enabled: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              HSTS Max Age (seconds)
            </label>
            <input
              type="number"
              value={Math.round((settings.securityHeaders?.strictTransportSecurity?.maxAge || 31536000) / 1000)}
              onChange={(e) => handleSettingChange('securityHeaders', 'strictTransportSecurity', { ...settings.securityHeaders?.strictTransportSecurity, maxAge: parseInt(e.target.value) * 1000 })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable X-Content-Type-Options
            </label>
            <input
              type="checkbox"
              checked={settings.securityHeaders?.xContentTypeOptions?.enabled || false}
              onChange={(e) => handleSettingChange('securityHeaders', 'xContentTypeOptions', { ...settings.securityHeaders?.xContentTypeOptions, enabled: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              X-Frame-Options Action
            </label>
            <select
              value={settings.securityHeaders?.xFrameOptions?.action || 'SAMEORIGIN'}
              onChange={(e) => handleSettingChange('securityHeaders', 'xFrameOptions', { ...settings.securityHeaders?.xFrameOptions, action: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            >
              <option value="DENY">DENY</option>
              <option value="SAMEORIGIN">SAMEORIGIN</option>
              <option value="ALLOW-FROM">ALLOW-FROM</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Referrer Policy
            </label>
            <select
              value={settings.securityHeaders?.referrerPolicy?.policy || 'strict-origin-when-cross-origin'}
              onChange={(e) => handleSettingChange('securityHeaders', 'referrerPolicy', { ...settings.securityHeaders?.referrerPolicy, policy: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            >
              <option value="no-referrer">No Referrer</option>
              <option value="strict-origin">Strict Origin</option>
              <option value="strict-origin-when-cross-origin">Strict Origin When Cross-Origin</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enable Permissions Policy
            </label>
            <input
              type="checkbox"
              checked={settings.securityHeaders?.permissionsPolicy?.enabled || false}
              onChange={(e) => handleSettingChange('securityHeaders', 'permissionsPolicy', { ...settings.securityHeaders?.permissionsPolicy, enabled: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
        </div>
      </div>

      <div className="flex space-x-4">
        <button
          onClick={saveSettings}
          disabled={loading}
          className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 transition-colors duration-200 flex items-center justify-center space-x-2"
        >
          {loading ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
          <span>Save Security Settings</span>
        </button>
        
        <button
          onClick={resetToOptimalSettings}
          disabled={loading}
          className="px-4 py-3 bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white rounded-lg disabled:opacity-50 transition-colors duration-200 flex items-center justify-center space-x-2"
        >
          <RotateCcw className="h-5 w-5" />
          <span>Reset to Optimal</span>
        </button>
      </div>
    </div>
  );

  const renderRecaptchaSettings = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
        <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">reCAPTCHA Settings</h4>
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Configure reCAPTCHA to protect your forms from spam and abuse. Select which pages should display reCAPTCHA.
        </p>
      </div>

      {/* reCAPTCHA Enable/Disable */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Enable reCAPTCHA</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Turn on reCAPTCHA protection for your website
            </p>
          </div>
          <button
            onClick={() => setRecaptchaSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              recaptchaSettings.enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                recaptchaSettings.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* reCAPTCHA Configuration */}
      {recaptchaSettings.enabled && (
        <div className="space-y-6">
          {/* Site Key */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              reCAPTCHA Site Key
            </label>
            <input
              type="text"
              value={recaptchaSettings.siteKey}
              onChange={(e) => setRecaptchaSettings(prev => ({ ...prev, siteKey: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter your reCAPTCHA site key"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Get your site key from the Google reCAPTCHA admin console
            </p>
          </div>

          {/* Secret Key */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              reCAPTCHA Secret Key
            </label>
            <input
              type="password"
              value={recaptchaSettings.secretKey}
              onChange={(e) => setRecaptchaSettings(prev => ({ ...prev, secretKey: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter your reCAPTCHA secret key"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Keep your secret key secure and never expose it in client-side code
            </p>
          </div>

          {/* Page Selection */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Select Pages for reCAPTCHA</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Choose which pages should display reCAPTCHA verification
            </p>
            
            <div className="space-y-4">
              {[
                { key: 'login', label: 'Login Page', description: 'Protect user login attempts' },
                { key: 'register', label: 'Registration Page', description: 'Prevent spam registrations' },
                { key: 'forgotPassword', label: 'Forgot Password Page', description: 'Secure password reset requests' },
                { key: 'contact', label: 'Contact Form', description: 'Prevent spam contact submissions' },
                { key: 'resetPassword', label: 'Reset Password Page', description: 'Secure password reset completion' }
              ].map((page) => (
                <div key={page.key} className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id={`recaptcha-${page.key}`}
                    checked={recaptchaSettings.pages[page.key]}
                    onChange={(e) => setRecaptchaSettings(prev => ({
                      ...prev,
                      pages: { ...prev.pages, [page.key]: e.target.checked }
                    }))}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <div className="flex-1">
                    <label htmlFor={`recaptcha-${page.key}`} className="text-sm font-medium text-gray-900 dark:text-white">
                      {page.label}
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{page.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={saveRecaptchaSettings}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <RefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="-ml-1 mr-2 h-4 w-4" />
                  Save reCAPTCHA Settings
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderMonitoring = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Activity className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Security Events</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.totalSecurityEvents || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-8 w-8 text-red-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Recent Events (24h)</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.recentSecurityEvents || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Lock className="h-8 w-8 text-orange-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Blocked IPs</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.blockedIPsCount || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Sessions</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{activeSessions.length || 0}</p>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={loadData}
        disabled={loading}
        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white rounded-lg disabled:opacity-50 transition-colors duration-200 flex items-center space-x-2"
      >
        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        <span>Refresh Stats</span>
      </button>
    </div>
  );

  const renderLogs = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Security Logs</h3>
        <button
          onClick={loadLogs}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition-colors duration-200"
        >
          Load Logs
        </button>
      </div>
      
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Event</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">IP Address</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{log.event_type}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{log.ip_address}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    <pre className="text-xs">{JSON.stringify(log.details, null, 2)}</pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderIPManagement = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">IP Management</h3>
        <button
          onClick={loadBlockedIPs}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition-colors duration-200"
        >
          Load Blocked IPs
        </button>
      </div>
      
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">IP Address</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Blocked Until</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {blockedIPs.map((ip) => (
                <tr key={ip.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{ip.ip_address}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{ip.reason}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(ip.blocked_until).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                         <button
                       onClick={() => unblockIP(ip.ip_address)}
                       className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-200"
                     >
                       <Unlock className="h-4 w-4" />
                     </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSessions = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Active Sessions</h3>
        <button
          onClick={loadActiveSessions}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition-colors duration-200"
        >
          Load Sessions
        </button>
      </div>
      
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">IP Address</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Connected At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {activeSessions.map((session) => (
                <tr key={session.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {session.name} ({session.email})
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{session.ip_address}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(session.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                         <button
                       onClick={() => terminateSession(session.id)}
                       className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-200"
                     >
                       <Lock className="h-4 w-4" />
                     </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderTesting = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
        <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">Security Testing</h4>
        <p className="text-sm text-yellow-700 dark:text-yellow-300">
          Test various security features to ensure they are working correctly.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => testSecurity('rate_limiting')}
          className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
        >
          <div className="flex items-center">
            <Activity className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 mr-2 sm:mr-3" />
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">Test Rate Limiting</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Verify rate limiting is working</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => testSecurity('sql_injection')}
          className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
        >
          <div className="flex items-center">
            <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 mr-2 sm:mr-3" />
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">Test SQL Injection Protection</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Check SQL injection detection</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => testSecurity('xss_protection')}
          className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
        >
          <div className="flex items-center">
            <Eye className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 mr-2 sm:mr-3" />
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">Test XSS Protection</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Verify XSS protection is active</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => testSecurity('brute_force')}
          className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
        >
          <div className="flex items-center">
            <Lock className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600 mr-2 sm:mr-3" />
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">Test Brute Force Protection</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Check brute force detection</p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Shield className="h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-3 text-blue-600" />
            Security Management
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Manage and monitor security features to protect your application
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-8 bg-white dark:bg-gray-800 rounded-t-lg">
          <nav className="-mb-px flex overflow-x-auto scrollbar-hide px-4 sm:px-6">
            <div className="flex space-x-2 sm:space-x-8 min-w-max">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center transition-colors duration-200 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                  </button>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          {activeTab === 'settings' && renderSettings()}
          {activeTab === 'recaptcha' && renderRecaptchaSettings()}
          {activeTab === 'monitoring' && renderMonitoring()}
          {activeTab === 'logs' && renderLogs()}
          {activeTab === 'ip-management' && renderIPManagement()}
          {activeTab === 'sessions' && renderSessions()}
          {activeTab === 'testing' && renderTesting()}
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div className={`mt-4 p-3 rounded-lg flex items-center ${
            saveStatus === 'success' 
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
          }`}>
            {saveStatus === 'success' ? (
              <CheckCircle className="h-4 w-4 mr-2" />
            ) : (
              <AlertTriangle className="h-4 w-4 mr-2" />
            )}
            {saveMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityManagement;
